import { useState, useEffect } from 'react';
import useFeatureFlags from '../services/featureToggle/useFeatureFlags';
import { CPM_DUPLICATE_REQUEST_CHECK } from '../constants/FeatureToggleKeys';
import { getLenderRequestsByProject } from '../services/LenderRequestService';
import { CLOSED_STATUSES, REQUEST_STATUSES } from '../constants/RequestStatuses';

const EXCLUDED_STATUSES = [
  ...CLOSED_STATUSES,
  REQUEST_STATUSES.UNRESPONSIVE,
];

export const normalizeRequestType = (value) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

export const getRequestTypeValue = (request) => request?.requestTypeName ?? request?.request ?? request?.requestType;

const useDuplicateRequestCheck = (projectId, phaseId = null, requestTypeName = null) => {
  const { isFeatureEnabled, flagUpdateCounter } = useFeatureFlags([CPM_DUPLICATE_REQUEST_CHECK]);

  const [blockingRequest, setBlockingRequest] = useState(null);
  const [warningRequest, setWarningRequest] = useState(null);
  const [matchingRequests, setMatchingRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stable serialised key so the useEffect dep array reacts to array content changes
  const requestTypeKey = Array.isArray(requestTypeName)
    ? requestTypeName
      .map(normalizeRequestType)
      .filter(Boolean)
      .sort()
      .join('|')
    : normalizeRequestType(requestTypeName);

  useEffect(() => {
    if (!isFeatureEnabled(CPM_DUPLICATE_REQUEST_CHECK)) {
      return;
    }

    setIsLoading(true);

    (async () => {
      try {
        const requests = await getLenderRequestsByProject(projectId);

        const passData = JSON.parse(sessionStorage.getItem('passData') || '{}');
        const currentLdap = passData.user;
        const currentLenderId = sessionStorage.getItem('lenderId') || passData.sellerServicerCoNo;

        const normalizedRequestedTypes = Array.isArray(requestTypeName)
          ? requestTypeName.map(normalizeRequestType).filter(Boolean)
          : null;
        const normalizedRequestedType = Array.isArray(requestTypeName)
          ? null
          : normalizeRequestType(requestTypeName);

        const matchesType = (r) => {
          if (requestTypeName == null) return true;
          const normalizedRequestType = normalizeRequestType(getRequestTypeValue(r));
          if (!normalizedRequestType) return false;
          if (Array.isArray(requestTypeName)) return normalizedRequestedTypes.includes(normalizedRequestType);
          return normalizedRequestType === normalizedRequestedType;
        };

        const blockingCandidates = requests.filter(
          (r) =>
            !EXCLUDED_STATUSES.includes(r.statusName) &&
            String(r.projectId) === String(projectId) &&
            (phaseId == null || String(r.phaseId) === String(phaseId)) &&
            matchesType(r)
        );
        blockingCandidates.sort((a, b) => b.requestId - a.requestId);
        setMatchingRequests(blockingCandidates);

        const blockingReq = blockingCandidates.find(
          (r) =>
            String(r.lenderId) === String(currentLenderId) &&
            r.requestCreatedByLdapId === currentLdap
        ) ?? null;

        const warningReq = blockingReq
          ? null
          : (blockingCandidates.find(
              (r) =>
                String(r.lenderId) === String(currentLenderId) &&
                r.requestCreatedByLdapId !== currentLdap
            ) ?? null);

        if (blockingReq) {
          console.warn('[DuplicateRequestCheck] blocking request found', {
            projectId,
            phaseId,
            requestId: blockingReq.requestId,
            lenderId: currentLenderId,
          });
        } else if (warningReq) {
          console.warn('[DuplicateRequestCheck] warning request found', {
            projectId,
            phaseId,
            requestId: warningReq.requestId,
            lenderId: currentLenderId,
          });
        }

        setBlockingRequest(blockingReq);
        setWarningRequest(warningReq);
      } catch (err) {
        console.error('[DuplicateRequestCheck] API call failed', err);
        setError(err);
        setBlockingRequest(null);
        setWarningRequest(null);
        setMatchingRequests([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [projectId, phaseId, requestTypeKey, flagUpdateCounter]); // intentional: currentLdap/currentLenderId are read fresh from sessionStorage on each run

  return { blockingRequest, warningRequest, matchingRequests, isLoading, error };
};

export default useDuplicateRequestCheck;
